import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, RefreshCw, Coffee, UtensilsCrossed, Cookie, Moon } from "lucide-react";

const MEALS = [
  { key: "breakfast", label: "Breakfast", icon: Coffee, bg: "#FFE600", fg: "#0A0A0A" },
  { key: "lunch", label: "Lunch", icon: UtensilsCrossed, bg: "#00CC66", fg: "#FFFFFF" },
  { key: "snacks", label: "Evening Snacks", icon: Cookie, bg: "#00E5FF", fg: "#0A0A0A" },
  { key: "dinner", label: "Dinner", icon: Moon, bg: "#FF4D4D", fg: "#FFFFFF" },
];

const EMPTY = { breakfast: { time: "", items: [] }, lunch: { time: "", items: [] }, snacks: { time: "", items: [] }, dinner: { time: "", items: [] } };
const EMPTY_RAW = { breakfast: "", lunch: "", snacks: "", dinner: "" };

export default function MenuTab() {
  const [menu, setMenu] = useState(EMPTY);
  // Raw textarea buffers — kept verbatim so space + Enter never get stripped mid-typing.
  const [rawItems, setRawItems] = useState(EMPTY_RAW);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/menu");
      const next = {
        breakfast: data.breakfast || EMPTY.breakfast,
        lunch: data.lunch || EMPTY.lunch,
        snacks: data.snacks || EMPTY.snacks,
        dinner: data.dinner || EMPTY.dinner,
      };
      setMenu(next);
      setRawItems({
        breakfast: (next.breakfast.items || []).join("\n"),
        lunch: (next.lunch.items || []).join("\n"),
        snacks: (next.snacks.items || []).join("\n"),
        dinner: (next.dinner.items || []).join("\n"),
      });
    } catch {
      toast.error("Failed to load menu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setMealTime = (mealKey, value) => {
    setMenu((m) => ({ ...m, [mealKey]: { ...m[mealKey], time: value } }));
  };

  const setRaw = (mealKey, text) => {
    setRawItems((r) => ({ ...r, [mealKey]: text }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Parse raw buffers into items[] ONCE, on save. Split by newline and drop
      // only fully-empty lines — preserving spaces inside each dish name.
      const parse = (text) => text.split("\n").map((s) => s.replace(/\s+$/, "")).filter((s) => s.length > 0);
      const payload = {
        breakfast: { ...menu.breakfast, items: parse(rawItems.breakfast) },
        lunch: { ...menu.lunch, items: parse(rawItems.lunch) },
        snacks: { ...menu.snacks, items: parse(rawItems.snacks) },
        dinner: { ...menu.dinner, items: parse(rawItems.dinner) },
      };
      await api.put("/menu", payload);
      setMenu(payload);
      toast.success("Menu saved — visible on the public homepage");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="menu-tab">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="font-heading font-black text-2xl tracking-tighter">Today's Food Menu</h3>
          <p className="text-sm font-medium text-[#4A4A4A]">
            One dish per line — Enter starts a new dish, Space works normally. Changes go live on Save.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={load}
            data-testid="menu-reload-button"
            className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
          >
            <RefreshCw size={14} /> Reload
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="menu-save-button"
            className="inline-flex items-center gap-2 font-bold text-sm px-5 py-2 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save menu"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="brutal-card p-8 text-center font-medium text-[#4A4A4A]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {MEALS.map(({ key, label, icon: Icon, bg, fg }) => (
            <div
              key={key}
              data-testid={`menu-editor-${key}`}
              style={{
                backgroundColor: bg, color: fg,
                border: "2px solid #0A0A0A", borderRadius: "0.75rem",
                boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)", padding: "1.5rem",
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon size={22} strokeWidth={2.5} />
                  <h4 className="font-heading font-black text-2xl">{label}</h4>
                </div>
              </div>
              <label className="block mt-4">
                <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">Time</span>
                <input
                  data-testid={`menu-${key}-time`}
                  value={menu[key].time}
                  onChange={(e) => setMealTime(key, e.target.value)}
                  className="w-full px-3 py-2 border-2 border-[#0A0A0A] rounded-xl bg-white text-[#0A0A0A] font-medium"
                  placeholder="e.g. 7:30 – 9:30 AM"
                />
              </label>
              <label className="block mt-3">
                <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">
                  Items (one per line)
                </span>
                <textarea
                  data-testid={`menu-${key}-items`}
                  value={rawItems[key]}
                  onChange={(e) => setRaw(key, e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 border-2 border-[#0A0A0A] rounded-xl bg-white text-[#0A0A0A] font-medium whitespace-pre-wrap"
                  placeholder={"Idli sambar\nMasala dosa\nFilter coffee"}
                />
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
